import type { Size, Vec2 } from './model/types';

/**
 * Pure viewport maths. Kept free of canvas/DOM references so zoom, pan and fit
 * are unit-testable and reusable by any engine implementation.
 *
 * Convention: a viewport transform maps document coordinates to screen
 * coordinates as `screen = doc * zoom + pan`. This matches the canvas viewport
 * transform `[zoom, 0, 0, zoom, pan.x, pan.y]`.
 */

export const MIN_ZOOM = 0.02;
export const MAX_ZOOM = 64;
export const ZOOM_STEPS = [
  0.02, 0.05, 0.08, 0.12, 0.16, 0.25, 0.33, 0.5, 0.66, 1, 1.5, 2, 3, 4, 6, 8, 12, 16,
];

export interface Viewport {
  zoom: number;
  pan: Vec2;
}

export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return 1;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

export function documentToViewport(viewport: Viewport, point: Vec2): Vec2 {
  return {
    x: point.x * viewport.zoom + viewport.pan.x,
    y: point.y * viewport.zoom + viewport.pan.y,
  };
}

export function viewportToDocument(viewport: Viewport, point: Vec2): Vec2 {
  return {
    x: (point.x - viewport.pan.x) / viewport.zoom,
    y: (point.y - viewport.pan.y) / viewport.zoom,
  };
}

/**
 * Zoom by `factor` while keeping the document point under `anchor` (a viewport
 * coordinate) visually fixed. This is what makes wheel-zoom feel anchored.
 */
export function zoomAround(viewport: Viewport, factor: number, anchor: Vec2): Viewport {
  const zoom = clampZoom(viewport.zoom * factor);
  const doc = viewportToDocument(viewport, anchor);
  return {
    zoom,
    pan: { x: anchor.x - doc.x * zoom, y: anchor.y - doc.y * zoom },
  };
}

/** Snap to the nearest predefined zoom step, moving in the requested direction. */
export function nextZoomStep(zoom: number, direction: 1 | -1): number {
  if (direction > 0) {
    const next = ZOOM_STEPS.find((step) => step > zoom + 1e-9);
    return next ?? MAX_ZOOM;
  }
  const reversed = [...ZOOM_STEPS].reverse();
  const prev = reversed.find((step) => step < zoom - 1e-9);
  return prev ?? MIN_ZOOM;
}

/**
 * Viewport that centres `docSize` inside `hostSize`. Never upscales past 100 % —
 * a small image should not fill the screen and look blurry.
 */
export function fitViewport(docSize: Size, hostSize: Size, padding = 32): Viewport {
  const availableW = Math.max(1, hostSize.width - padding * 2);
  const availableH = Math.max(1, hostSize.height - padding * 2);
  const zoom = clampZoom(
    Math.min(1, Math.min(availableW / docSize.width, availableH / docSize.height)),
  );
  return {
    zoom,
    pan: {
      x: Math.round((hostSize.width - docSize.width * zoom) / 2),
      y: Math.round((hostSize.height - docSize.height * zoom) / 2),
    },
  };
}

/** Viewport showing the document at exactly 100 %, centred. */
export function actualSizeViewport(docSize: Size, hostSize: Size): Viewport {
  return {
    zoom: 1,
    pan: {
      x: Math.round((hostSize.width - docSize.width) / 2),
      y: Math.round((hostSize.height - docSize.height) / 2),
    },
  };
}

export function panBy(viewport: Viewport, delta: Vec2): Viewport {
  return { zoom: viewport.zoom, pan: { x: viewport.pan.x + delta.x, y: viewport.pan.y + delta.y } };
}

export function zoomPercent(zoom: number): number {
  return Math.round(zoom * 100);
}

export function zoomLabel(zoom: number): string {
  return `${zoomPercent(zoom)}%`;
}
